import * as React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { useTheme } from '../../theme';
import { TravellerTypes } from '../../Traveller/types';
import { TravellerProfile } from '@websky/graphql';
import { concatPassengerNames, getFirstCharName, getPassengerNames } from '../../utils';
import { CdnIcon } from '@shared/ui';

export interface Props {
	className?: string;
	headerClassName?: string;
	profile?: TravellerProfile;
	editText?: string;
	editLink?: string;
	type?: TravellerTypes;
	inline?: boolean;
	confirmContacts?: boolean;
	onClick?: () => void;
	showArrow?: boolean;
}

const UserCard: React.FC<Props> = ({
	className,
	profile,
	editText,
	editLink,
	inline,
	headerClassName,
	confirmContacts,
	type,
	onClick,
	showArrow
}) => {
	const { UserCard: css } = useTheme('UserCard');
	const { t } = useTranslation('Account');

	const { firstName, lastName, middleName } = getPassengerNames(profile);

	const headerLabel = React.useMemo(() => {
		if (type === TravellerTypes.CompanionEmpty) {
			return t('Add companion');
		}

		if (firstName && lastName) {
			return concatPassengerNames({ lastName, firstName, middleName });
		}

		return t('Welcome!');
	}, [type, firstName, lastName, middleName]);

	const avatarIcon = React.useMemo(() => {
		if (type === TravellerTypes.CompanionEmpty) {
			return <CdnIcon icon="Action/Add" />;
		}

		if (firstName && lastName) {
			return <span>{getFirstCharName({ lastName, firstName })}</span>;
		}

		return <CdnIcon icon="Info/Passenger" />;
	}, [type, firstName, lastName]);

	return (
		<div
			className={cn(className, css.wrapper, {
				[css.inline]: inline,
				[css.companion]: type === TravellerTypes.CompanionEmpty || type === TravellerTypes.CompanionFilled,
				[css.empty]: type === TravellerTypes.CompanionEmpty
			})}
			onClick={onClick ? onClick : () => {}}
		>
			<div className={cn(css.header, headerClassName)}>
				<div className={css.header_content}>
					<div className={css.avatar}>{avatarIcon}</div>
					<div className={css.info}>
						<div className={css.name}>{headerLabel}</div>
						{confirmContacts && <div className={css.notification}>{t('Please confirm your contacts')}</div>}
					</div>

					{(!!editText || showArrow) && (
						<span className={css.edit}>
							<span className={css.edit__text}>{editText}</span>
							{showArrow && <CdnIcon icon="Arrow/Chevron_right" />}
						</span>
					)}
				</div>
			</div>
		</div>
	);
};

export default UserCard;
